import type { CSSProperties } from 'react';
import { ACCENT_COLORS, BACKGROUND_COLORS, BORDER_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';
import { MONEY_CELL_STYLE, MONEY_COLUMN_MIN_WIDTH } from '@/document-render/presentation/blocks/moneyCellStyles';

export type LineItemAlign = 'left' | 'right';

export interface LineItemColumn {
  key: 'description' | 'quantity' | 'unit' | 'rate' | 'amount' | 'actions';
  label: string;
  align: LineItemAlign;
  /** Share of the table width, in percent (fixed layout). */
  width: number;
}

/**
 * The quote canvas' line-items table, column by column. Description takes the slack, the money columns
 * keep `MONEY_COLUMN_MIN_WIDTH` so `$XX,XXX.XX` never wraps, and Actions is a narrow gutter for the
 * overflow menu.
 */
export const LINE_ITEM_COLUMN: Record<LineItemColumn['key'], LineItemColumn> = {
  description: { key: 'description', label: 'Description', align: 'left', width: 38 },
  quantity: { key: 'quantity', label: 'Qty', align: 'right', width: 10 },
  unit: { key: 'unit', label: 'Unit', align: 'left', width: 12 },
  rate: { key: 'rate', label: 'Unit price', align: 'right', width: 16 },
  amount: { key: 'amount', label: 'Amount', align: 'right', width: 16 },
  actions: { key: 'actions', label: 'Actions', align: 'right', width: 8 }
};

export const LINE_ITEM_COLUMNS: readonly LineItemColumn[] = [
  LINE_ITEM_COLUMN.description,
  LINE_ITEM_COLUMN.quantity,
  LINE_ITEM_COLUMN.unit,
  LINE_ITEM_COLUMN.rate,
  LINE_ITEM_COLUMN.amount,
  LINE_ITEM_COLUMN.actions
];

export const LINE_ITEMS_TABLE_STYLE: CSSProperties = { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' };

export function lineItemColStyle(column: LineItemColumn): CSSProperties {
  const isMoney = column.key === 'rate' || column.key === 'amount';
  return { width: `${column.width}%`, ...(isMoney ? { minWidth: MONEY_COLUMN_MIN_WIDTH } : {}) };
}

/** Same header the Rate Schedule table wears: a faint band, 10px bold uppercase with tracking. */
export function lineItemHeadCellStyle(align: LineItemAlign, fontSize: number | string = 10): CSSProperties {
  return {
    padding: '8px 10px',
    fontSize,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: TEXT_COLORS.ON_FAINT,
    background: BORDER_COLORS.FAINT,
    textAlign: align,
    overflowWrap: 'break-word',
    wordBreak: 'normal'
  };
}

export function lineItemBodyCellStyle(align: LineItemAlign): CSSProperties {
  return {
    padding: '6px 10px',
    borderBottom: `1px solid ${BORDER_COLORS.LIGHT}`,
    textAlign: align,
    verticalAlign: 'middle',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    ...(align === 'right' ? MONEY_CELL_STYLE : {})
  };
}

/** The inline editor that opens in a cell: it fills the cell and reads as a field, not a native box. */
export function lineItemInputStyle(align: LineItemAlign): CSSProperties {
  return {
    width: '100%',
    boxSizing: 'border-box',
    font: 'inherit',
    color: 'inherit',
    textAlign: align,
    padding: '4px 6px',
    border: `1px solid ${BORDER_COLORS.LIGHT}`,
    borderRadius: 0,
    background: BACKGROUND_COLORS.WHITE
  };
}

/** "+ Add …" / "Edit": an empty cell's way in. Quiet until used. */
export function lineItemAffordanceStyle(align: LineItemAlign): CSSProperties {
  return {
    width: '100%',
    background: 'none',
    border: 0,
    padding: 0,
    font: 'inherit',
    color: TEXT_COLORS.MUTED,
    cursor: 'pointer',
    textAlign: align
  };
}

export const LINE_ITEM_ADD_BUTTON_STYLE: CSSProperties = {
  font: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  color: ACCENT_COLORS.DEFAULT,
  background: BACKGROUND_COLORS.WHITE,
  border: `1px solid ${BORDER_COLORS.LIGHT}`,
  padding: '4px 10px',
  cursor: 'pointer'
};

export const LINE_ITEM_MENU_TRIGGER_STYLE: CSSProperties = {
  width: 24,
  height: 24,
  padding: 0,
  font: 'inherit',
  fontSize: 16,
  lineHeight: 1,
  color: TEXT_COLORS.MUTED,
  background: 'none',
  border: 0,
  cursor: 'pointer'
};

export const LINE_ITEM_MENU_STYLE: CSSProperties = {
  position: 'absolute',
  right: 0,
  top: '100%',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 120,
  background: BACKGROUND_COLORS.WHITE,
  border: `1px solid ${BORDER_COLORS.LIGHT}`,
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)'
};

export function lineItemMenuItemStyle(disabled: boolean): CSSProperties {
  return {
    font: 'inherit',
    fontSize: 13,
    textAlign: 'left',
    padding: '6px 12px',
    background: 'none',
    border: 0,
    color: disabled ? TEXT_COLORS.MUTED : TEXT_COLORS.NEAR_BLACK,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.55 : 1
  };
}
